import React, { useState } from 'react';
import { Star, AlertTriangle, X, GripVertical } from 'lucide-react';

const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABEL = { mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday', sun: 'Sunday' };

export const StaffTray = ({ staff, loads }) => (
  <div className="rounded-xl border border-[#DED8EB] bg-white p-2 flex flex-wrap gap-1.5" data-testid="planner-staff-tray">
    {staff.map(s => (
      <div key={s.id} draggable
           onDragStart={e => { e.dataTransfer.setData('text/plain', `staff:${s.id}`); e.dataTransfer.effectAllowed = 'copy'; }}
           className="shrink-0 cursor-grab active:cursor-grabbing rounded-full border border-[#D9C9F2] bg-[#F7F2FD] px-2.5 py-1 flex items-center gap-1.5 hover:border-[#6B3FA0] select-none"
           data-testid={`tray-staff-${s.id}`} title={`Available: ${s.available_days.join(', ')}${s.avg_weekly_hours ? ` · avg ${s.avg_weekly_hours}h/wk` : ''}`}>
        <GripVertical className="h-3 w-3 text-[#B9B2C9]" />
        <span className="text-xs font-bold text-[#2D1B4E]">{s.name}</span>
        <span className="text-[9px] font-black text-[#6B3FA0] bg-white rounded-full px-1.5 border border-[#E8E2F2]">{loads[s.id] || 0}</span>
        <span className="flex gap-px">
          {DAYS.map(d => <span key={d} className={`w-1.5 h-3 rounded-sm ${s.available_days.includes(d) ? 'bg-[#9B7ED8]' : 'bg-gray-200'}`} />)}
        </span>
      </div>
    ))}
  </div>
);

const DropCell = ({ onDrop, children, testid, className = '' }) => {
  const [over, setOver] = useState(false);
  return (
    <div onDragOver={e => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
         onDrop={e => { e.preventDefault(); setOver(false); onDrop(e.dataTransfer.getData('text/plain')); }}
         className={`${className} ${over ? 'ring-2 ring-[#6B3FA0] ring-offset-1' : ''}`} data-testid={testid}>
      {children}
    </div>
  );
};

export const DayGrid = ({ parts, draft, staffById, staged, onDropStaff, onClearPrimary, onRemoveAlt, lineLabels }) => {
  const noDays = parts.filter(p => !p.attend_days.length);
  const cols = [...DAYS, ...(noDays.length ? ['none'] : [])];
  const renderCard = (p, day) => {
    const a = draft[p.name] || { primary_id: '', alternate_ids: [] };
    const prim = staffById[a.primary_id];
    const isStaged = staged.includes(p.name);
    const dayMiss = day !== 'none' && prim && !prim.available_days.includes(day);
    return (
      <DropCell key={p.name} onDrop={(d) => d.startsWith('staff:') && onDropStaff(p.name, d.slice(6))}
                testid={`cell-${day}-${p.name}`}
                className={`rounded-lg border bg-white p-1.5 ${isStaged ? 'border-[#6B3FA0] border-dashed bg-[#FBF8FF]' : 'border-[#E8E2F2]'}`}>
        <div className="text-[11px] font-bold text-[#2D1B4E] leading-tight flex items-center gap-1">
          {p.name}
          {dayMiss && <AlertTriangle className="h-3 w-3 text-amber-500 shrink-0" title={`${prim.name} not available ${day}`} />}
        </div>
        {p.service_line && <div className="text-[8px] font-bold uppercase text-[#9B8FAE]">{lineLabels?.[p.service_line] || p.service_line}</div>}
        {prim ? (
          <div className={`mt-1 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${dayMiss ? 'bg-amber-50 text-amber-800 border border-amber-200' : 'bg-[#6B3FA0] text-white'}`}>
            <Star className="h-2.5 w-2.5" /> {prim.name}
            <button onClick={() => onClearPrimary(p.name)} className="opacity-70 hover:opacity-100" data-testid={`clear-primary-${p.name}`}><X className="h-2.5 w-2.5" /></button>
          </div>
        ) : <div className="mt-1 text-[9px] italic text-[#C7BFD6]">drop staff here</div>}
        {a.alternate_ids.length > 0 && (
          <div className="mt-0.5 flex flex-wrap gap-0.5">
            {a.alternate_ids.map(x => (
              <span key={x} className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-[#6B3FA0] bg-[#F0EAFA] rounded-full px-1.5 py-px">
                {staffById[x]?.name}
                <button onClick={() => onRemoveAlt(p.name, x)}><X className="h-2 w-2" /></button>
              </span>
            ))}
          </div>
        )}
      </DropCell>
    );
  };
  return (
    <div className={`grid gap-1.5 ${noDays.length ? 'grid-cols-8' : 'grid-cols-7'}`} data-testid="planner-daygrid">
      {cols.map(day => {
        const dayParts = day === 'none' ? noDays : parts.filter(p => p.attend_days.includes(day));
        return (
          <div key={day} className={`rounded-xl border p-1.5 min-h-[120px] ${day === 'none' ? 'border-dashed border-[#C7BFD6] bg-white' : 'border-[#DED8EB] bg-[#FBFAFD]'}`}>
            <div className={`text-[10px] font-black uppercase tracking-wider px-1 mb-1.5 ${day === 'none' ? 'text-[#8E85A0]' : 'text-[#6B3FA0]'}`}>
              {day === 'none' ? 'No set days' : DAY_LABEL[day]} <span className="text-[#B9B2C9]">({dayParts.length})</span>
            </div>
            <div className="space-y-1.5">
              {dayParts.map(p => renderCard(p, day))}
              {dayParts.length === 0 && <div className="text-[9px] italic text-[#C7BFD6] px-1">no one attends</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const StaffBoard = ({ staff, parts, draft, staged, loads, onMove, onClearPrimary, lineLabels }) => {
  const byStaff = {};
  const unassigned = [];
  parts.forEach(p => {
    const pid = (draft[p.name] || {}).primary_id;
    if (pid) byStaff[pid] = [...(byStaff[pid] || []), p];
    else unassigned.push(p);
  });
  const Chip = ({ p }) => (
    <span draggable onDragStart={e => { e.dataTransfer.setData('text/plain', `part:${p.name}`); }}
          className={`cursor-grab active:cursor-grabbing inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 border select-none ${staged.includes(p.name) ? 'border-[#6B3FA0] border-dashed bg-[#FBF8FF] text-[#6B3FA0]' : 'bg-[#F7F4FB] border-[#E8E2F2] text-[#5A4D6E]'}`}
          data-testid={`board-part-${p.name}`} title={`Attends: ${p.attend_days.join(', ') || '—'}`}>
      <GripVertical className="h-2.5 w-2.5 text-[#C7BFD6]" /> {p.name}
    </span>
  );
  return (
    <div className="space-y-2" data-testid="planner-staffboard">
      <DropCell onDrop={(d) => d.startsWith('part:') && onClearPrimary(d.slice(5))} testid="board-unassigned"
                className="rounded-xl border-2 border-dashed border-[#DED8EB] bg-white p-2.5">
        <div className="text-[10px] font-black uppercase tracking-wider text-[#8E85A0] mb-1">No primary assigned ({unassigned.length}) — drop here to clear</div>
        <div className="flex flex-wrap gap-1">{unassigned.map(p => <Chip key={p.name} p={p} />)}</div>
      </DropCell>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {staff.map(s => (
          <DropCell key={s.id} onDrop={(d) => d.startsWith('part:') && onMove(d.slice(5), s.id)} testid={`board-staff-${s.id}`}
                    className="rounded-xl border border-[#DED8EB] bg-white p-2.5 min-h-[72px]">
            <div className="flex items-center gap-1.5 mb-1">
              <span className="text-sm font-bold text-[#2D1B4E]">{s.name}</span>
              <span className="text-[9px] font-black text-white bg-[#6B3FA0] rounded-full px-1.5">{loads[s.id] || 0}</span>
              <span className="ml-auto flex gap-px">
                {DAYS.map(d => <span key={d} className={`w-1.5 h-3 rounded-sm ${s.available_days.includes(d) ? 'bg-[#9B7ED8]' : 'bg-gray-200'}`} title={d} />)}
              </span>
            </div>
            {(s.service_lines || []).length > 0 && (
              <div className="text-[8px] font-bold uppercase text-[#9B8FAE] mb-1">{s.service_lines.map(l => lineLabels?.[l] || l).join(' · ')}</div>
            )}
            <div className="flex flex-wrap gap-1">
              {(byStaff[s.id] || []).map(p => <Chip key={p.name} p={p} />)}
              {!(byStaff[s.id] || []).length && <span className="text-[10px] italic text-[#C7BFD6]">drop participants here</span>}
            </div>
          </DropCell>
        ))}
      </div>
    </div>
  );
};
